import * as React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';

import * as Edge from '../../../core';
import { getTeamsUserCanTrain } from '../../../store/permissions/selector';
import { CanTrainTeamsBanner } from '../../../components/teamBanner';
import errorLoadingWrapperHOC from '../../../components/errorLoadingWrapper/errorLoadingWrapperHOC';
import ExerciseList from '../exerciseList';
import StartButton from '../startButton';
import AppPageContainer from '../../../components/pageContainer';
import PageTitle from '../../../components/pageTitle';
import TrainingAreaNav from '../trainingAreaNav';
import RenderExercise from '../renderExercise';
import DeviceProfiles from '../deviceProfiles';
import { ExerciseMetadataService } from '../../../services/exerciseMetadataService';
import { AppState } from '../../../store';
import { activeSessionSelector } from '../../../store/session/selector';
import SessionExpiredModal from '../../../components/sessionExpiredModal';

import './index.scss';

export interface OpenGymTrainingProps {
	currentTeam?: Edge.Models.Team;
	getRemainingTime: (now?: Date) => moment.Duration | undefined;
	hasCompletedEvaluation?: boolean;
}

interface OpenGymTrainingState {
	currentExercise?: Edge.Models.ExerciseConfiguration;
	sessionExpired?: boolean;
}

export class OpenGymTraining extends React.PureComponent<OpenGymTrainingProps, OpenGymTrainingState> {
	constructor(props: OpenGymTrainingProps) {
		super(props);
		this.state = {};
	}
	public render() {
		const { currentTeam } = this.props;
		const { currentExercise, sessionExpired } = this.state;
		if (currentExercise) {
			return (
				<RenderExercise
					completeText="Continue Training"
					gameMode={Edge.Models.GameMode.OpenGym}
					exercise={currentExercise}
					onComplete={this.onComplete}
					onAbort={this.onComplete} /* we treat abort and complete the same */
					operationName="Training"
				/>
			);
		}
		return (
			<>
				<PageTitle title="Open Gym" />
				<TrainingAreaNav hasCompletedEvaluation={this.props.hasCompletedEvaluation} />
				<AppPageContainer>
					<DeviceProfiles />
					<CanTrainTeamsBanner />
					{currentTeam && this.renderBody()}
					{sessionExpired && <SessionExpiredModal onClose={this.closeSessionExpired} />}
				</AppPageContainer>
			</>
		);
	}
	private closeSessionExpired = () => {
		this.setState({ sessionExpired: false });
	};
	private onComplete = () => {
		const remaining = this.props.getRemainingTime();
		this.setState({ currentExercise: undefined, sessionExpired: !(remaining && remaining.asMilliseconds() > 0) });
	};
	private renderBody = () => {
		const { hasCompletedEvaluation } = this.props;
		const configs = this.getSortedOpenGymExercises(hasCompletedEvaluation !== false);
		return (
			<div className="open-gym">
				<p className="open-gym-subtext">
					Train on your own terms. Choose any drill and customize the settings to fit your goals.
				</p>
				<ExerciseList
					configs={configs}
					isExerciseLocked={(config: Edge.Models.ExerciseConfiguration) =>
						this.isExerciseLocked(config, hasCompletedEvaluation !== false)
					}
					renderExerciseLineButton={(config: Edge.Models.ExerciseConfiguration) =>
						this.isExerciseLocked(config, hasCompletedEvaluation !== false) ? null : (
							<StartButton isTraining onClick={() => this.trainNow(config)} />
						)
					}
				/>
			</div>
		);
	};
	private isExerciseLocked = (
		config: Edge.Models.ExerciseConfiguration,
		hasCompletedEvaluation: boolean
	): boolean => {
		if (this.props.currentTeam && this.props.currentTeam.type.id === Edge.Models.TeamTypeId.StationMode) {
			return false;
		}
		if (hasCompletedEvaluation) {
			return false;
		}
		const exercise = ExerciseMetadataService.getMetadata(config);
		return !!(exercise && exercise.usesGlasses);
	};
	private getSortedOpenGymExercises = (hasCompletedEvaluation: boolean): Edge.Models.ExerciseConfiguration[] => {
		const configs = ExerciseMetadataService.getOpenGymExercises();
		return [...configs].sort((a, b) => {
			const aLocked = this.isExerciseLocked(a, hasCompletedEvaluation);
			const bLocked = this.isExerciseLocked(b, hasCompletedEvaluation);
			if (aLocked === bLocked) {
				return 0;
			}
			return aLocked ? 1 : -1;
		});
	};
	private trainNow = (config: Edge.Models.ExerciseConfiguration) => {
		this.setState({
			currentExercise: config,
		});
	};
}

function mapStateToProps(state: AppState) {
	const { currentTeam } = getTeamsUserCanTrain(state);
	const { getRemainingTime } = activeSessionSelector(state);
	return {
		currentTeam,
		getRemainingTime,
	};
}

export default connect(mapStateToProps)(errorLoadingWrapperHOC(OpenGymTraining));
